import { useCallback, useEffect, useState } from 'react';

import { OAuthProvider } from '@front/common/src/types/commons';
import { OauthConnectParams } from '@front/common/src/queries/user-queries';
import { errorNotifier } from '@front/common/src/errors/error-notifier';

import { translate } from '@front/i18n';

import useScript, { ScriptStatus } from '@front/web-ui/src/hooks/use-script';

import Config from '../utils/config';

const t = translate('oauth');

const useOAuth = (oauthConnect: (data: OauthConnectParams) => void) => {
  const [thirdPartyCalled, setThirdPartyCalled] = useState<string>();
  const [submitted, doSetSubmitted] = useState<string>();
  const [error, setError] = useState<string>();
  const [facebookInitialized, setFacebookInitialized] = useState(false);
  const facebookScriptStatus = useScript('https://connect.facebook.net/en_US/sdk.js');

  const initialize = useCallback((status: ScriptStatus, initializationCallback: () => void) => {
    if (status === 'ready') {
      try {
        initializationCallback();
      } catch (err) {
        if (err instanceof Error) {
          errorNotifier.notify(err, 'warning');
        }
      }
    }
  }, []);

  const oauthSubmit = useCallback(
    (omniAuthData: OauthConnectParams['omniAuthData']) => {
      doSetSubmitted(omniAuthData.provider);
      oauthConnect({ omniAuthData });
    },
    [oauthConnect],
  );

  useEffect(() => {
    initialize(facebookScriptStatus, () => {
      FB.init({
        appId: Config.FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v12.0',
        status: true,
      });
      setFacebookInitialized(true);
    });
  }, [facebookScriptStatus, initialize]);

  const setSubmitted = useCallback((value: typeof submitted) => {
    doSetSubmitted(value);
    setThirdPartyCalled(undefined);
  }, []);

  const submitCallback = useCallback(async (provider: OAuthProvider, callback: () => void) => {
    doSetSubmitted(undefined);
    setThirdPartyCalled(provider);
    setError(undefined);
    try {
      await callback();
    } catch (err) {
      setError(t('.errors.not_connected', { provider: t(`.providers.${provider}`) }));
      if (err instanceof Error) {
        errorNotifier.notify(err);
      }
    }
  }, []);

  const facebookLogin = useCallback(() => {
    submitCallback('facebook', () => {
      FB.login(
        ({ authResponse, status }) => {
          if (status === 'connected' && authResponse.accessToken) {
            oauthSubmit({ provider: 'facebook', accessToken: authResponse.accessToken!, ...authResponse });
          } else {
            setError(t('.errors.not_connected', { provider: t('.providers.facebook') }));
          }
        },
        { scope: 'email', return_scopes: true },
      );
    });
  }, [oauthSubmit, submitCallback]);

  const triggerGoogleSignInBackendProcess = (accessToken: string) => {
    submitCallback('google', () => {
      oauthSubmit({ accessToken, provider: 'google' });
    });
  };

  return {
    facebookLogin,
    facebookInitialized,
    triggerGoogleSignInBackendProcess,
    error,
    thirdPartyCalled,
    submitted,
    setSubmitted,
  };
};

export default useOAuth;
